/**
 * 项目级元素 API（角色、场景、道具）
 * 对接后端 /api/v1/projects/{parent_project_id}/characters | locations | props
 */

import apiClient from './client';
import type { LinkedScreenplayUsage } from '@/types/resource';
import type { ResourceSortField, ResourceSortOrder } from '@/lib/resources/resource-sort';

// ---------- 请求体（驼峰格式） ----------

export interface ProjectCharacterCreatePayload {
  name: string;
  description?: string | null;
  roleType?: number;
  isOffscreen?: boolean;
  metaData?: Record<string, unknown>;
  tags?: string[]; // 标签名称列表（如：['便装', '正装']）
}

export interface ProjectCharacterUpdatePayload {
  name?: string;
  description?: string | null;
  characterImageUrl?: string | null;
  roleType?: number | string;
  isOffscreen?: boolean;
  gender?: string | null;
  visualAge?: string | null;
  race?: string | null;
  personality?: string | null;
  appearance?: string | null;
  voice?: {
    voiceId: string | null;
    voiceName: string | null;
  };
  metaData?: Record<string, unknown>;
}

export interface ProjectLocationCreatePayload {
  name: string;
  location?: string | null;
  description?: string | null;
  metaData?: Record<string, unknown>;
  tags?: string[]; // 标签名称列表（如：['白天', '夜晚']）
}

export interface ProjectLocationUpdatePayload {
  name?: string;
  location?: string | null;
  description?: string | null;
  locationImageUrl?: string | null;
  timeOfDay?: string | null;
  weather?: string | null;
  season?: string | null;
  atmosphere?: string | null;
  realWorldLocation?: string | null;
  metaData?: Record<string, unknown>;
}

export interface ProjectPropCreatePayload {
  name: string;
  description?: string | null;
  metaData?: Record<string, unknown>;
  tags?: string[]; // 标签名称列表（如：['可用', '损坏']）
}

export interface ProjectPropUpdatePayload {
  name?: string;
  description?: string | null;
  propImageUrl?: string | null;
  propType?: string | null;
  usage?: string | null;
  ownerCharacter?: string | null;
  ownerLocation?: string | null;
  metaData?: Record<string, unknown>;
}

export type ProjectElementCoverType = 'character' | 'location' | 'prop';

export interface ElementCoverImageUpdatePayload {
  imageUrl: string;
}

export interface ProjectElementDeleteImpactResponse {
  linkedScreenplays: LinkedScreenplayUsage[];
}

// ---------- 响应体（驼峰格式） ----------

export interface ProjectCharacterResponse {
  characterId: string;
  projectId: string;
  name: string;
  description?: string | null;
  characterImageUrl?: string | null;
  /** 默认装扮的定稿缩略图，characterImageUrl 为空时使用 */
  defaultThumbnailUrl?: string | null;
  roleType: number;
  isOffscreen: boolean;
  orderIndex: number;
  hasTags: boolean;
  gender?: string | null;
  visualAge?: string | null;
  personality?: string | null;
  appearance?: string | null;
  voice?: {
    voiceId: string | null;
    voiceName: string | null;
  } | null;
  metaData: Record<string, unknown>;
  tags?: ElementTag[]; // 标签列表（可选，通过 includeTags 参数控制）
  createdAt: string;
  updatedAt: string;
}

export interface ProjectLocationResponse {
  locationId: string;
  projectId: string;
  name: string;
  location?: string | null;
  description?: string | null;
  locationImageUrl?: string | null;
  /** 默认装扮缩略图URL */
  defaultThumbnailUrl?: string | null;
  orderIndex: number;
  hasTags: boolean;
  metaData: Record<string, unknown>;
  tags?: ElementTag[]; // 标签列表（可选，通过 includeTags 参数控制）
  createdAt: string;
  updatedAt: string;
}

export interface ProjectPropResponse {
  propId: string;
  projectId: string;
  name: string;
  description?: string | null;
  propImageUrl?: string | null;
  /** 默认装扮缩略图URL */
  defaultThumbnailUrl?: string | null;
  orderIndex: number;
  hasTags: boolean;
  metaData: Record<string, unknown>;
  tags?: ElementTag[]; // 标签列表（可选，通过 includeTags 参数控制）
  createdAt: string;
  updatedAt: string;
}

// ---------- 列表响应（仅 ID、parentProjectId、name、description、coverUrl） ----------

export interface ElementTagListItem {
  tagId: string;
  tagLabel: string;
  isDefault: boolean;
  selectedResourceId?: string | null;
  tagImageUrl?: string | null;
  resourceUrl?: string | null;
}

export interface ProjectCharacterListResponse {
  characterId: string;
  parentProjectId: string;
  name: string;
  description?: string | null;
  coverUrl?: string | null;
  isOffscreen: boolean;
  tagCount: number;
  defaultTagId?: string | null;
  createdAt: string;
  updatedAt: string;
  tags?: ElementTagListItem[];
  linkedScreenplays?: LinkedScreenplayUsage[];
  /** 当前身份能否改这条角色。缺省按能改。 */
  canManageAsset?: boolean;
}

export interface ProjectLocationListResponse {
  locationId: string;
  parentProjectId: string;
  name: string;
  description?: string | null;
  coverUrl?: string | null;
  tagCount: number;
  defaultTagId?: string | null;
  createdAt: string;
  updatedAt: string;
  tags?: ElementTagListItem[];
  linkedScreenplays?: LinkedScreenplayUsage[];
  /** 当前身份能否改这条场景。缺省按能改。 */
  canManageAsset?: boolean;
}

export interface ProjectPropListResponse {
  propId: string;
  parentProjectId: string;
  name: string;
  description?: string | null;
  coverUrl?: string | null;
  tagCount: number;
  defaultTagId?: string | null;
  createdAt: string;
  updatedAt: string;
  tags?: ElementTagListItem[];
  linkedScreenplays?: LinkedScreenplayUsage[];
  /** 当前身份能否改这条道具。缺省按能改。 */
  canManageAsset?: boolean;
}

export interface ProjectElementListResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ProjectCharactersListParams {
  onlyOwnedByCurrentEpisode?: boolean;
  onlyLinkedToCurrentEpisode?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
  includeTags?: boolean;
  gender?: string;
  race?: string;
  visualAge?: string;
  timeOfDay?: string;
  propType?: string;
  roleType?: number;
  isOffscreen?: boolean;
  sortBy?: ResourceSortField;
  sortOrder?: ResourceSortOrder;
}

// ---------- 详情响应（包含标签和资源，驼峰格式） ----------

export interface ElementTag {
  tagId: string;
  tagLabel: string;
  description: string | null;
  displayOrder: number;
  resourceCount: number;
  thumbnailUrl: string | null;
  fileUrl: string | null;
  isDefault?: boolean;
  voiceId?: string | null;
  voiceName?: string | null;
  gender?: string | null;
  visualAge?: string | null;
  race?: string | null;
  personality?: string | null;
}

export interface CharacterDetailResponse {
  characterId: string;
  projectId: string;
  screenplayId: string;
  name: string;
  description: string | null;
  characterImageUrl?: string | null;
  defaultThumbnailUrl?: string | null;
  roleType: string;
  isOffscreen: boolean;
  lineCount: number;
  appearanceCount: number;
  tagCount: number;
  defaultTagId: string | null;
  resourceCount: number;
  metaData: Record<string, unknown>;
  /** @deprecated 后端已移除，标签数据请使用 element-tags API */
  tags?: ElementTag[];
}

export interface LocationDetailResponse {
  locationId: string;
  projectId: string;
  screenplayId: string;
  name: string;
  location: string | null;
  description: string | null;
  locationImageUrl?: string | null;
  defaultThumbnailUrl?: string | null;
  tagCount: number;
  defaultTagId: string | null;
  resourceCount: number;
  metaData: Record<string, unknown>;
  /** @deprecated 后端已移除，标签数据请使用 element-tags API */
  tags?: ElementTag[];
}

export interface PropDetailResponse {
  propId: string;
  projectId: string;
  screenplayId: string;
  name: string;
  description: string | null;
  propImageUrl?: string | null;
  defaultThumbnailUrl?: string | null;
  tagCount: number;
  defaultTagId: string | null;
  resourceCount: number;
  metaData: Record<string, unknown>;
  /** @deprecated 后端已移除，标签数据请使用 element-tags API */
  tags?: ElementTag[];
}

// ---------- API ----------

export const projectElementsApi = {
  // ========== 角色相关 ==========

  /**
   * 获取项目角色列表（分页，支持搜索、includeTags）
   */
  getCharacters(
    parentProjectId: string,
    params?: ProjectCharactersListParams
  ): Promise<ProjectElementListResponse<ProjectCharacterListResponse>> {
    return apiClient.get(`/projects/${parentProjectId}/characters`, {
      params: {
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 100,
        search: params?.search,
        includeTags: params?.includeTags,
        onlyOwnedByCurrentEpisode: params?.onlyOwnedByCurrentEpisode,
        onlyLinkedToCurrentEpisode: params?.onlyLinkedToCurrentEpisode,
        gender: params?.gender,
        race: params?.race,
        visualAge: params?.visualAge,
        roleType: params?.roleType,
        isOffscreen: params?.isOffscreen,
        sortBy: params?.sortBy,
        sortOrder: params?.sortOrder,
      },
    });
  },

  createCharacter(
    parentProjectId: string,
    payload: ProjectCharacterCreatePayload
  ): Promise<ProjectCharacterResponse> {
    return apiClient.post(`/projects/${parentProjectId}/characters`, payload);
  },

  createLocation(
    parentProjectId: string,
    payload: ProjectLocationCreatePayload
  ): Promise<ProjectLocationResponse> {
    return apiClient.post(`/projects/${parentProjectId}/locations`, payload);
  },

  // ========== 场景相关 ==========

  /**
   * 获取项目场景列表（分页，支持搜索、includeTags）
   */
  getLocations(
    parentProjectId: string,
    params?: ProjectCharactersListParams
  ): Promise<ProjectElementListResponse<ProjectLocationListResponse>> {
    return apiClient.get(`/projects/${parentProjectId}/locations`, {
      params: {
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 100,
        search: params?.search,
        includeTags: params?.includeTags,
        onlyOwnedByCurrentEpisode: params?.onlyOwnedByCurrentEpisode,
        onlyLinkedToCurrentEpisode: params?.onlyLinkedToCurrentEpisode,
        timeOfDay: params?.timeOfDay,
        sortBy: params?.sortBy,
        sortOrder: params?.sortOrder,
      },
    });
  },

  createProp(
    parentProjectId: string,
    payload: ProjectPropCreatePayload
  ): Promise<ProjectPropResponse> {
    return apiClient.post(`/projects/${parentProjectId}/props`, payload);
  },

  // ========== 道具相关 ==========

  /**
   * 获取项目道具列表（分页，支持搜索、includeTags）
   */
  getProps(
    parentProjectId: string,
    params?: ProjectCharactersListParams
  ): Promise<ProjectElementListResponse<ProjectPropListResponse>> {
    return apiClient.get(`/projects/${parentProjectId}/props`, {
      params: {
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 100,
        search: params?.search,
        includeTags: params?.includeTags,
        onlyOwnedByCurrentEpisode: params?.onlyOwnedByCurrentEpisode,
        onlyLinkedToCurrentEpisode: params?.onlyLinkedToCurrentEpisode,
        propType: params?.propType,
        sortBy: params?.sortBy,
        sortOrder: params?.sortOrder,
      },
    });
  },

  // 详情接口
  getCharacterDetail(
    parentProjectId: string,
    characterId: string
  ): Promise<CharacterDetailResponse> {
    return apiClient.get(`/projects/${parentProjectId}/characters/${characterId}`);
  },

  getLocationDetail(parentProjectId: string, locationId: string): Promise<LocationDetailResponse> {
    return apiClient.get(`/projects/${parentProjectId}/locations/${locationId}`);
  },

  getPropDetail(parentProjectId: string, propId: string): Promise<PropDetailResponse> {
    return apiClient.get(`/projects/${parentProjectId}/props/${propId}`);
  },

  // 删除接口
  getCharacterDeleteImpact(
    parentProjectId: string,
    characterId: string
  ): Promise<ProjectElementDeleteImpactResponse> {
    return apiClient.get(`/projects/${parentProjectId}/characters/${characterId}/delete-impact`);
  },

  getLocationDeleteImpact(
    parentProjectId: string,
    locationId: string
  ): Promise<ProjectElementDeleteImpactResponse> {
    return apiClient.get(`/projects/${parentProjectId}/locations/${locationId}/delete-impact`);
  },

  getPropDeleteImpact(
    parentProjectId: string,
    propId: string
  ): Promise<ProjectElementDeleteImpactResponse> {
    return apiClient.get(`/projects/${parentProjectId}/props/${propId}/delete-impact`);
  },

  getDeleteImpact(
    parentProjectId: string,
    type: 'character' | 'location' | 'prop',
    elementId: string
  ): Promise<ProjectElementDeleteImpactResponse> {
    if (type === 'character') return this.getCharacterDeleteImpact(parentProjectId, elementId);
    if (type === 'location') return this.getLocationDeleteImpact(parentProjectId, elementId);
    return this.getPropDeleteImpact(parentProjectId, elementId);
  },

  deleteCharacter(
    parentProjectId: string,
    characterId: string,
    options?: { confirmCrossScreenplayUsage?: boolean }
  ): Promise<void> {
    return apiClient.delete(`/projects/${parentProjectId}/characters/${characterId}`, {
      params: { confirmCrossScreenplayUsage: options?.confirmCrossScreenplayUsage },
    });
  },

  deleteLocation(
    parentProjectId: string,
    locationId: string,
    options?: { confirmCrossScreenplayUsage?: boolean }
  ): Promise<void> {
    return apiClient.delete(`/projects/${parentProjectId}/locations/${locationId}`, {
      params: { confirmCrossScreenplayUsage: options?.confirmCrossScreenplayUsage },
    });
  },

  deleteProp(
    parentProjectId: string,
    propId: string,
    options?: { confirmCrossScreenplayUsage?: boolean }
  ): Promise<void> {
    return apiClient.delete(`/projects/${parentProjectId}/props/${propId}`, {
      params: { confirmCrossScreenplayUsage: options?.confirmCrossScreenplayUsage },
    });
  },

  // 更新接口
  updateCharacter(
    parentProjectId: string,
    characterId: string,
    payload: ProjectCharacterUpdatePayload
  ): Promise<ProjectCharacterResponse> {
    return apiClient.put(`/projects/${parentProjectId}/characters/${characterId}`, payload);
  },

  updateLocation(
    parentProjectId: string,
    locationId: string,
    payload: ProjectLocationUpdatePayload
  ): Promise<ProjectLocationResponse> {
    return apiClient.put(`/projects/${parentProjectId}/locations/${locationId}`, payload);
  },

  updateProp(
    parentProjectId: string,
    propId: string,
    payload: ProjectPropUpdatePayload
  ): Promise<ProjectPropResponse> {
    return apiClient.put(`/projects/${parentProjectId}/props/${propId}`, payload);
  },

  updateElementCoverImage(
    projectId: string,
    elementType: ProjectElementCoverType,
    elementId: string,
    payload: ElementCoverImageUpdatePayload
  ): Promise<{
    elementType: ProjectElementCoverType;
    elementId: string;
    imageUrl: string;
  }> {
    return apiClient.put(
      `/projects/${projectId}/elements/${elementType}/${elementId}/cover-image`,
      payload
    );
  },

  /**
   * 获取筛选选项聚合数据
   */
  getFilterOptions(
    parentProjectId: string,
    elementType?: string
  ): Promise<{
    character?: { gender: string[]; race: string[]; visualAge: string[] };
    location?: { timeOfDay: string[] };
    prop?: { propType: string[] };
  }> {
    return apiClient.get(`/projects/${parentProjectId}/elements/filter-options`, {
      params: { elementType },
    });
  },
};
